import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { serverNow } from '../../lib/clock'
import { lotteryPhaseAt } from '../../lib/lotteryTimeline'
import type { LotteryPhase, LotteryTimeline } from '../../lib/lotteryTimeline'

/* ---------------------------------------------------------------------------
 * The whole synchronisation story: one latched anchor, one loop, one monotonic
 * clamp, one re-anchor moment — plus the two things that switch all of that
 * off, a reduced-motion branch and a local replay.
 *
 * It sits beside its only caller in components/lottery/ rather than in lib/,
 * the way usePickFlash.ts sits beside PickFlash.tsx.
 * ------------------------------------------------------------------------ */

// Corrections under this are noise: the offset ring's median moving by a
// sample, plus one-way latency. Snapping on them would put a visible step into
// a 60fps animation and buy nothing against a 100ms fastest beat.
//
// A guess rather than a measurement — above the ring's plausible jitter, below
// the shortest thing a viewer would notice mid-draw. If tab restores feel jumpy
// on a phone, raise it; if a restored tab visibly lags the others, lower it.
const REANCHOR_MIN_MS = 250

// Nothing is moving under reduced motion, so 60fps buys nothing. The tightest
// beat lotteryTimeline can produce is 670ms (150 funnel + 230 tube + 190 climb
// + a 100ms gap), so a 100ms poll is at most ~15% of a beat late — well inside
// the spread between two phones on the same wifi, which this page already
// tolerates.
const REDUCED_TICK_MS = 100

export type LotteryMode = 'sync' | 'replay'

interface Anchor {
  perf: number
  base: number
}

export interface LotteryClock {
  /** Discrete phase. Re-renders only when something declarative changed, so
   *  React runs ~15 times across the draw rather than ~700. */
  phase: LotteryPhase | null
  /** Live elapsed ms, read once per frame by the canvas and the flight layer. */
  elapsedRef: RefObject<number>
  /** 'sync' follows the server anchor. 'replay' follows a local one and refuses
   *  re-anchoring. */
  mode: LotteryMode
  /** The OS asked for reduced motion. Read once, at mount. */
  reduced: boolean
  /** 0 for the synchronized run, +1 per replay. Key imperative controllers on it. */
  runId: number
  /** null until the synchronized run has reached the crown — nothing to replay
   *  before then. */
  replay: (() => void) | null
}

export function useLotteryClock(
  /** draft.lottery_revealed_at — the ISO string, NOT the draft object. */
  revealedAt: string | null,
  /** Must be referentially stable: useMemo it on the team count. */
  timeline: LotteryTimeline,
  /** Per-frame side effects. Held in a ref so a new closure does not restart the loop. */
  onFrame?: (elapsedMs: number, phase: LotteryPhase) => void,
): LotteryClock {
  const [phase, setPhase] = useState<LotteryPhase | null>(null)
  const [mode, setMode] = useState<LotteryMode>('sync')
  const [runId, setRunId] = useState(0)

  // Read once and never subscribed. A change mid-draw would tear the canvas
  // down between two frames of a flight, and the honest reset for an OS setting
  // is a reload — which is also what makes the flag observable in a test. The
  // declarative half of the degradation is live either way, through the global
  // clamp in styles/application.css.
  //
  // Both optional calls are load-bearing: jsdom implements no matchMedia at
  // all, so this falls to false there, which is what lets every lottery test
  // render the stage without a stub.
  const [reduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,
  )

  const elapsedRef = useRef(0)
  const anchorRef = useRef<Anchor | null>(null)
  const keyRef = useRef('')
  const onFrameRef = useRef(onFrame)
  onFrameRef.current = onFrame
  // reanchor() is called from a listener that outlives a replay, so it reads
  // the mode through a ref rather than the value it closed over.
  const modeRef = useRef(mode)
  modeRef.current = mode

  const runReplay = useCallback(() => {
    // The monotonic clamp is per *run*: left at doneMs from the synchronized
    // run, every subsequent Math.max would return doneMs and the replay would
    // render one settled frame and stop. The effect below is what actually
    // prevents that — it assigns elapsedRef from `base` on every run — so this
    // line is belt and braces for the window between here and the effect.
    elapsedRef.current = 0
    // Published synchronously as well, so the render between this call and the
    // effect's first read does not still show twelve landed rows — which would
    // fire the landing flourish on pick 1 as the replay begins.
    keyRef.current = ''
    setPhase(lotteryPhaseAt(0, timeline))
    setMode('replay')
    // Bumping runId is what re-runs the tick effect; the effect re-anchors at
    // base 0 because the mode it now reads is 'replay'.
    setRunId((n) => n + 1)
  }, [timeline])

  useEffect(() => {
    if (!revealedAt) {
      anchorRef.current = null
      keyRef.current = ''
      setPhase(null)
      return
    }
    // NaN propagates silently through the anchor arithmetic and would turn every
    // comparison in lotteryPhaseAt false — a blank rail rather than a crash.
    const revealedAtMs = Date.parse(revealedAt)
    if (Number.isNaN(revealedAtMs)) return

    // ONE read of the server offset per run. getServerOffset() is the median of
    // a five-element ring that every incoming cable event mutates, so a later
    // sample can move it tens of ms either way. Read per frame it would step
    // visibly at 60fps and — the actual killer — could move `elapsed`
    // *backwards*; `landed` is a pure function of `elapsed`, so rows would
    // un-fill and the reveal would run in reverse.
    //
    // This is NOT the trap CLAUDE.md forbids ("the client never decrements a
    // local counter for the clock"). That protects the open-ended pick clock,
    // which can sit for ten minutes in a throttled tab with unbounded drift.
    // This is a bounded ~12.1s one-shot whose only correctness requirement is a
    // synchronised *start*, and re-anchoring on visibility preserves the trap's
    // intent rather than defeating it. The countdown *before* the reveal is
    // still useCountdown, server-anchored, untouched. performance.now() is also
    // monotonic and immune to the NTP steps Date.now() takes.
    //
    // A replay is anchored at elapsed 0 on the local clock instead. That is
    // also the guard for the second re-anchor path — lottery_revealed_at going
    // null -> set — which is this effect's own `revealedAt` dependency.
    const base = mode === 'replay' ? 0 : serverNow() - revealedAtMs
    anchorRef.current = { perf: performance.now(), base }
    elapsedRef.current = base // may be negative — standby is a real state

    const reanchor = () => {
      // Re-anchoring is a jump, and both of its triggers are jumps the viewer is
      // already expecting. During a replay it is neither: it would compute
      // elapsed from lottery_revealed_at, land past doneMs, and snap the viewer
      // back to the settled board mid-replay.
      if (modeRef.current === 'replay') return
      const fresh = serverNow() - revealedAtMs
      const current = anchorRef.current
      if (current) {
        const drift = fresh - (current.base + (performance.now() - current.perf))
        if (Math.abs(drift) < REANCHOR_MIN_MS) return
      }
      anchorRef.current = { perf: performance.now(), base: fresh }
    }

    // Re-anchor moment 1 of 2. rAF is frozen in a background tab, so `elapsed`
    // is stale by definition the moment we return — and useDraftChannel
    // refetches on this same event, so a jump here sits inside a jump the user
    // already caused.
    const onVisible = () => {
      if (document.visibilityState === 'visible') reanchor()
    }
    document.addEventListener('visibilitychange', onVisible)
    const teardown = () => document.removeEventListener('visibilitychange', onVisible)

    let stopped = false
    const read = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      // Belt and braces on top of the latch: a re-anchor may only move elapsed
      // forward. A backwards correction is absorbed by standing still until
      // wall time catches up, which is invisible; un-filling rows is not.
      const elapsed = Math.max(elapsedRef.current, anchor.base + (performance.now() - anchor.perf))
      elapsedRef.current = elapsed

      const next = lotteryPhaseAt(elapsed, timeline)
      onFrameRef.current?.(elapsed, next) // no-op under reduced motion: there is no rig

      const key = `${next.phase}:${next.landed.length}:${next.ledPick ?? ''}`
      if (key !== keyRef.current) {
        keyRef.current = key
        setPhase(next)
      }
      // A settled page must not hold a loop open for the rest of the evening.
      // In replay this is also the handoff — the effect below sees phase 'done'
      // with mode 'replay' and hands the anchor back to the server.
      if (elapsed >= timeline.doneMs) stopped = true
    }

    read()
    if (stopped) return teardown

    if (reduced) {
      const id = setInterval(() => {
        read()
        if (stopped) clearInterval(id)
      }, REDUCED_TICK_MS)
      return () => {
        clearInterval(id)
        teardown()
      }
    }

    let raf = requestAnimationFrame(function frame() {
      read()
      if (!stopped) raf = requestAnimationFrame(frame)
    })
    return () => {
      cancelAnimationFrame(raf)
      teardown()
    }
    // mode and runId are dependencies because a replay changes neither
    // revealedAt nor timeline — without them the loop that just stopped at
    // doneMs would never restart and the button would do nothing.
  }, [revealedAt, timeline, reduced, mode, runId])

  // Replay is bounded, so it ends itself. Handing the anchor back to the server
  // here is what puts the viewer on the settled board without a second code path
  // for "finished replaying": the tick effect re-runs, recomputes base from
  // lottery_revealed_at, and lands far past crownMs — exactly the state a late
  // arrival gets.
  useEffect(() => {
    if (mode !== 'replay') return
    if (phase?.phase !== 'done') return
    elapsedRef.current = 0
    setMode('sync')
    setRunId((n) => n + 1)
  }, [mode, phase?.phase])

  return {
    phase,
    elapsedRef,
    mode,
    reduced,
    runId,
    // The same crownMs threshold the mount-time seek uses to decide "too late
    // to seek, render the settled board" — one threshold, one rule.
    replay:
      mode === 'sync' && (phase?.phase === 'crown' || phase?.phase === 'done') ? runReplay : null,
  }
}
